const BASIC_HTML_ENTITY_MAP = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: '\'',
  nbsp: ' ',
};

/**
 * Returns true when a string appears to contain HTML markup.
 */
export function looksLikeHtmlBody(value) {
  if (typeof value !== 'string') return false;
  return /<\/?[a-z][\s\S]*>/i.test(value) || /&(?:[a-z]+|#\d+|#x[0-9a-f]+);/i.test(value);
}

function decodeHtmlEntities(value) {
  return value.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, token) => {
    const normalized = String(token).toLowerCase();
    if (normalized.startsWith('#x')) {
      const codePoint = Number.parseInt(normalized.slice(2), 16);
      return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : match;
    }
    if (normalized.startsWith('#')) {
      const codePoint = Number.parseInt(normalized.slice(1), 10);
      return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : match;
    }
    return BASIC_HTML_ENTITY_MAP[normalized] ?? match;
  });
}

/**
 * Converts HTML-ish email content into plain text.
 */
export function htmlToPlainText(value) {
  if (typeof value !== 'string' || !value.trim()) return '';
  let text = value
    .replace(/\r\n?/g, '\n')
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/\s*(p|div|li|tr|h[1-6])\s*>/gi, '\n')
    .replace(/<\s*\/\s*td\s*>/gi, ' ')
    .replace(/<\s*(style|script|head)\b[\s\S]*?<\/\s*\1\s*>/gi, ' ')
    .replace(/<[^>]+>/g, ' ');

  text = decodeHtmlEntities(text)
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t\f\v]+/g, ' ')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();

  return text;
}

/**
 * Removes quoted-reply history so downstream intake sees only the newest text.
 */
export function stripQuotedReplyHistory(value) {
  if (typeof value !== 'string' || !value.trim()) return '';
  const lines = value.replace(/\r\n?/g, '\n').split('\n');
  const cutoff = lines.findIndex((line) => {
    const trimmed = line.trim();
    if (!trimmed) return false;
    const tripOrigin = /^from:\s*(?:\d+\s+|[^@]*\b(?:airport|terminal|ATL)\b)/i.test(trimmed) && !trimmed.includes('@');
    return (
      (/^from:\s/i.test(trimmed) && !tripOrigin)
      || /^-{2,}\s*original message\s*-{2,}$/i.test(trimmed)
      || /^on .+ wrote:$/i.test(trimmed)
      || /^>/.test(trimmed)
    );
  });
  return (cutoff === -1 ? lines : lines.slice(0, cutoff)).join('\n').trim();
}

/**
 * Normalizes inbound email text for storage/rendering.
 */
export function normalizeInboundEmailBody({ bodyContent = '', bodyContentType = '' } = {}) {
  const raw = typeof bodyContent === 'string' ? bodyContent : '';
  const contentType = typeof bodyContentType === 'string' ? bodyContentType.toLowerCase() : '';
  const plain = contentType === 'html' || looksLikeHtmlBody(raw)
    ? htmlToPlainText(raw)
    : raw.replace(/\r\n?/g, '\n').trim();
  return stripQuotedReplyHistory(plain);
}

export function normalizeInternetMessageId(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}
